import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import { useUsers } from '../api';
import type { UserRow } from '../types';
import { BadgeList } from '../components/BadgeList';
import { UserRowActions } from '../components/UserRowActions';
import { UserStatusBadge } from '../components/UserStatusBadge';

/**
 * Sub-user list for ROLE_USER. Same backend endpoint as the main user
 * list, scoped by the caller's role. Mirrors the main list columns
 * minus Parent (always the caller) and minus the Switch action.
 */
export function SubUserListPage() {
  const usersQuery = useUsers();
  const users = usersQuery.data?.users ?? [];

  /**
   * Column order: ID → Username → Type → Company → Status → Location →
   * Group → Module → (rest scrollable) → Actions (pinned right).
   */
  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'ID',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.userId}</span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <Link
            to={`/update-sub-user/${row.original.userId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.username}
          </Link>
        ),
      },
      {
        accessorKey: 'userType',
        header: 'Type',
        size: 100,
        cell: ({ row }) => <Badge variant="outline">{row.original.userType}</Badge>,
      },
      { accessorKey: 'company', header: 'Company' },
      {
        id: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => <UserStatusBadge user={row.original} />,
      },
      {
        id: 'locations',
        header: 'Location',
        enableSorting: false,
        cell: ({ row }) => {
          const list = row.original.locations ?? row.original.location ?? [];
          return <BadgeList items={list.map((l) => l.locationName)} />;
        },
      },
      {
        id: 'groups',
        header: 'Group',
        enableSorting: false,
        cell: ({ row }) => {
          const list = row.original.groups ?? row.original.group ?? [];
          return <BadgeList items={list.map((g) => g.groupName)} />;
        },
      },
      {
        id: 'modules',
        header: 'Module',
        enableSorting: false,
        cell: ({ row }) => {
          const list = row.original.modules ?? [];
          return <BadgeList items={list.map((m) => m.moduleName)} />;
        },
      },
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'credits',
        header: 'Voice Balance',
        size: 120,
        cell: ({ row }) => (
          <span className="tabular-nums">
            ₹{Number(row.original.credits ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'planName',
        header: 'Voice Plan',
        cell: ({ row }) => row.original.planName ?? '—',
      },
      {
        accessorKey: 'ttsCredits',
        header: 'TTS Balance',
        size: 110,
        cell: ({ row }) => (
          <span className="tabular-nums">
            ₹{Number(row.original.ttsCredits ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'ttsPlanName',
        header: 'TTS Plan',
        cell: ({ row }) => row.original.ttsPlanName ?? '—',
      },
      {
        accessorKey: 'accountType',
        header: 'Account',
        size: 130,
        cell: ({ row }) => (
          <Badge variant={row.original.accountType === 1 ? 'default' : row.original.accountType === 3 ? 'outline' : 'secondary'}>
              {row.original.accountType === 1 ? 'Transactional' : row.original.accountType === 3 ? 'Both' : 'Promotional'}
          </Badge>
        ),
      },
      {
        accessorKey: 'expiryDate',
        header: 'Expiry',
        size: 130,
        cell: ({ row }) => {
          const v = row.original.expiryDate;
          if (!v) return '—';
          const date = new Date(v.replace(' ', 'T'));
          return Number.isFinite(date.getTime()) ? formatDate(date) : v;
        },
      },
      {
        accessorKey: 'reqDate',
        header: 'Created',
        size: 170,
        cell: ({ row }) => formatDateTime(row.original.reqDate),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <UserRowActions
            user={row.original}
            editPathPrefix="/update-sub-user"
            hideSwitchUser
          />
        ),
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Team"
        description="Users you've created under your account."
        actions={
          <Button asChild>
            <Link to="/create-sub-user">
              <Plus className="h-4 w-4" />
              Add sub user
            </Link>
          </Button>
        }
      />
      <DataTable<UserRow, unknown>
        // v6 — picks up the FillPage flex layout.
        tableId="users:sub:v6"
        columns={columns}
        data={users}
        isLoading={usersQuery.isLoading}
        searchPlaceholder="Search sub users…"
        emptyTitle="No sub users yet"
        emptyDescription="Create a sub user to give a teammate access under your account."
        getRowId={(row) => String(row.userId)}
        exportFilename="sub-users.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />
    </FillPage>
  );
}
